import { Button } from "@/components/ui/button.tsx";
import { trace } from "@opentelemetry/api";
import { PageTitle } from "@packages/core-client/components/PageTitle.tsx";
import { useLogger } from "@squide/firefly";
import { useI18nextInstance } from "@squide/i18next";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useRouteError } from "react-router";
import { i18NextInstanceKey } from "../i18next.ts";

export function RootErrorBoundary() {
    const error = useRouteError();
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("RootErrorBoundary", { i18n: i18nextInstance });
    const logger = useLogger();

    useEffect(() => {
        if (!error) {
            return;
        }

        // oxlint-disable-next-line typescript/no-base-to-string -- a thrown non-Error is logged as-is; "[object Object]" for a plain object is the accepted floor
        const wrapped = error instanceof Error ? error : new Error(String(error));

        logger.withText("[shell] RootErrorBoundary caught an error:").withError(wrapped).error();

        trace.getActiveSpan()?.recordException(wrapped);
    }, [error, logger]);

    const handleGoHome = useCallback(() => {
        window.location.assign("/");
    }, []);

    return (
        <div className="bg-bg text-fg flex min-h-screen flex-col items-center justify-center px-6 text-center">
            <div role="alert" className="flex flex-col items-center">
                <PageTitle data-public>{t("title")}</PageTitle>
                <p className="text-muted-fg mt-2 max-w-md text-sm leading-normal" data-public>
                    {t("description")}
                </p>
            </div>
            <Button intent="secondary" size="sm" onPress={handleGoHome} className="border-border bg-overlay text-fg hover:bg-fg/4 mt-6 rounded-lg border px-4 py-2 text-sm font-medium transition-colors" data-public>
                {t("goHome")}
            </Button>
        </div>
    );
}
